<template>
  <section class="auth-register">
    <div class="container d-flex justify-content-center">
      <div class="col-12 col-sm-10 col-md-6">
        <BlocksCard>
          <h2 class="__heading">
            Register
          </h2>

          <transition name="fade">
            <!-- Registration Form -->
            <div v-if="!isLoading && (!registrationResponse || registrationResponse === 406)" :key="`registration-form`" class="auth-register__form" @keydown.enter.prevent="register">
              <div class="row auth-register__form">
                <div class="col-12 col-md-6 auth-register__form-email auth-register__form-block">
                  <ElementsInputsText v-model="firstName" v-model:is-valid="isValid" label="First Name" :rules="{fieldName: 'First name', rules: {required: true, minLength: 2, maxLength: 32}}" type="text" variant="underlined" />
                </div>
                <div class="col-12 col-md-6 auth-register__form-email auth-register__form-block">
                  <ElementsInputsText v-model="lastName" v-model:is-valid="isValid" label="Last Name" :rules="{fieldName: 'Last name', rules: {required: true, minLength: 2, maxLength: 32}}" type="text" variant="underlined" />
                </div>
                <div class="col-12 col-md-6 auth-register__form-email auth-register__form-block">
                  <ElementsInputsText v-model="email" v-model:is-valid="isValid" label="Email" type="text" :rules="{fieldName: 'Email', rules: {required: true, email: true, maxLength: 128, duplicateEmail: isEmailExists}}" variant="underlined" />
                </div>
                <div class="col-12 col-md-6 auth-register__form-block">
                  <ElementsInputsText v-model="password" v-model:is-valid="isValid" label="Password" type="password" :rules="{fieldName: 'Password', rules: {required: true, minLength: 6, aSymbolRequired: true, aNumberRequired: true}}" variant="underlined" />
                </div>
                <div class="col-md-12">
                  <ElementsRecaptcha ref="recaptchaRef" />
                </div>
              </div>
              <div class="row __action">
                <div class="col-12 auth-register__form-block __action-button">
                  <ElementsButtonsRegular label="Register" :loading="isLoading" @click="register" />
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <nuxt-link class="btn-text-link btn-register" to="/auth/login">
                    I have an account? Login
                  </nuxt-link>
                </div>
              </div>
              <div class="row auth-register__alternate-register-options">
                <div class="col-12">
                  <div class="line-text">
                    <span>OR</span>
                  </div>
                </div>
                <div class="col-12 auth-register__button-wrapper">
                  <AuthLoginFacebook v-model:loading="isLoading" v-model:response="registrationResponse" />
                  <AuthLoginGoogle v-model:loading="isLoading" v-model:response="registrationResponse" />
                </div>
              </div>
            </div>

            <!-- Registration Response -->
            <div v-else :key="`registration-response`" class="auth-register__response">
              <div class="row">
                <div v-if="isLoading" class="col-12">
                  <div class="d-flex justify-content-center">
                    <div class="auth-register__loading-spinner">
                      <img v-lazy-load  :data-src="spinner" alt="Loading">
                    </div>
                  </div>
                </div>
                <div class="auth-register__response-content">
                  <p v-if="registrationResponse && [403, 407, 500].includes(registrationResponse)">
                    Oops! Something went wrong. please try again later.
                  </p>
                  <p v-else-if="registrationResponse && registrationResponse === 200">
                    <strong>Registration Successful!</strong>
                    We've sent an email to your email address. Please check your inbox and click on the verification button to activate your account. Don't forget to check your spam folder if you can't find the email.
                  </p>
                </div>
              </div>
            </div>
          </transition>
        </BlocksCard>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {Ref} from 'vue';
import spinner from '~/assets/images/icons/dynamic-spinner.svg';

const firstName: Ref<string | null> = ref(null);
const lastName: Ref<string | null> = ref(null);
const email: Ref<string | null> = ref(null);
const password: Ref<string | null> = ref(null);
const isValid: Ref<string[]> = ref([]);
const isLoading: Ref<boolean> = ref(false);
const {$register} = useNuxtApp();
const registrationResponse: Ref<200 | 201 | 403 | 406 | 407 | 500 | null> = ref(null);
const isEmailExists: Ref<boolean> = ref(false);
const recaptchaRef: Ref<HTMLElement | null> = ref(null);

/**
 * Register
 */
function register() {
  // Return of the form is loading
  if (isLoading.value) {
    return;
  }

  isEmailExists.value = false;

  // Run validating by changing the value (fields have a watch on them and those will run the validation when isValid is changed)
  isValid.value = ['validate'];
  nextTick(async () => {
    if (!(isValid.value.filter((item) => item !== 'validate').length)) {
      isLoading.value = true;
      const token = await (recaptchaRef.value as any).validate();
      const response = await $register({
        firstName: firstName.value as string,
        lastName: lastName.value as string,
        email: email.value as string,
        password: password.value,
        token,
      });

      if (response.code === 406) {
        setTimeout(() => {
          isEmailExists.value = true;
          isValid.value = ['validate'];
        }, 100);
      }
      registrationResponse.value = response.code;
      isLoading.value = false;
    }
  });
}
</script>

<style lang="scss">
.auth-register {
  &__form {
    margin-top: 28px;

    &-block {
      margin-bottom: 28px;
      display: flex;
      justify-content: center;
    }
  }

  .btn-text-link {
    font-size: 14px;
    color: var(--primary);
    text-decoration: underline;
    display: block;
    padding-right: 12px;

    &.btn-forgot-password {
      text-align: right;
    }

    &.btn-register {
      margin-top: 20px;
      text-align: center;
    }
  }

  .auth-register__alternate-register-options {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 40px;

    .line-text {
      text-align: center;
      overflow: hidden;
      white-space: nowrap;
      background: #fff;
      display: flex;
      justify-content: center;

      span {
        background: #fff;
        z-index: 2;
        padding: 10px 20px; /* You may adjust this value */
        position: relative;
        width: fit-content;
      }

      &:before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        display: inline-block;
        width: 100%;
        height: 1px;
        background: #d5d5d5;
        z-index: 1;
      }
    }

    .auth-register__button-wrapper {
      margin-top: 20px;
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
    }
  }

  &__loading-spinner {
    margin: 12px;
    width: 32px;
    height: 32px;
  }

  &__response {
    &-content {
      strong {
        margin-right: 5px;
        color: green;
      }
      padding: 12px 12px;
      margin: 0 auto;
    }
  }
}
</style>
